import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { openTawkChat } from '../common/TawkTo';
import { getImageUrl } from '../../utils/imageUrl';
import { useTheme } from '../../utils/theme';
import './UserSidebar.css';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || ''}/api`;

const UserSidebar = ({ activeSection, setActiveSection, isOpen = false, onClose }) => {
  const { t } = useTranslation('user');
  const { isDark, toggleTheme } = useTheme();
  const [profileImage, setProfileImage] = useState(null);
  const [logoUrl, setLogoUrl] = useState('/logo192.png');

  // Get user info from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  // UX Best Practice: Display full name (first_name + last_name)
  // Properly capitalize: First letter uppercase, rest lowercase
  const capitalizeName = (name) => {
    if (!name) return '';
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };
  
  const getUserDisplayName = () => {
    if (user.first_name && user.last_name) {
      // Display full name: "Soufiane Rihani"
      return `${capitalizeName(user.first_name)} ${capitalizeName(user.last_name)}`.trim();
    }
    if (user.first_name) {
      // Only first name available
      return capitalizeName(user.first_name);
    }
    if (user.name) {
      // Use full name from name field
      const nameParts = user.name.split(' ');
      if (nameParts.length >= 2) {
        return `${capitalizeName(nameParts[0])} ${capitalizeName(nameParts.slice(1).join(' '))}`.trim();
      }
      return capitalizeName(nameParts[0]);
    }
    if (user.email) {
      return capitalizeName(user.email.split('@')[0]);
    }
    return t('user.profile.user');
  };
  
  const getUserRole = () => {
    if (user.role) {
      const roleKey = user.role.toLowerCase();
      return t(`user.profile.${roleKey}`, {
        defaultValue: user.role.charAt(0).toUpperCase() + user.role.slice(1),
      });
    }
    return t('user.profile.student');
  };
  
  const getUserInitials = () => {
    if (user.first_name && user.last_name) {
      return (user.first_name[0] + user.last_name[0]).toUpperCase();
    }
    if (user.first_name) {
      return user.first_name.substring(0, 2).toUpperCase();
    }
    if (user.name) {
      const parts = user.name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return user.name.substring(0, 2).toUpperCase();
    }
    if (user.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'ST';
  };
  
  const displayName = getUserDisplayName();
  const displayRole = getUserRole();
  const userInitials = getUserInitials();

  useEffect(() => {
    const syncProfilePhoto = () => {
      try {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        const photo = stored.profile_image || stored.profileImage || null;
        setProfileImage(photo ? (getImageUrl(photo) || photo) : null);
      } catch {
        setProfileImage(null);
      }
    };

    syncProfilePhoto();
    window.addEventListener('user-profile-updated', syncProfilePhoto);
    window.addEventListener('storage', syncProfilePhoto);
    return () => {
      window.removeEventListener('user-profile-updated', syncProfilePhoto);
      window.removeEventListener('storage', syncProfilePhoto);
    };
  }, []);

  // Load logo banner (from Banner Management) once
  useEffect(() => {
    fetch(`${API_BASE_URL}/banners?type=website&active=true`)
      .then((res) => res.json())
      .then((data) => {
        if (!data || !data.success || !Array.isArray(data.banners)) return;
        const logoBanner = data.banners.find((b) => (b.title || '').toLowerCase().includes('logo'));
        if (logoBanner && logoBanner.image_url) {
          setLogoUrl(logoBanner.image_url);
        }
      })
      .catch(() => {});
  }, []);

  const navSections = [
    {
      title: t('user.sidebar.learning'),
      items: [
        { id: 'dashboard', label: t('user.sidebar.dashboard'), icon: 'dashboard', badge: null },
        { id: 'explore-courses', label: t('user.sidebar.exploreCourses'), icon: 'explore', badge: null },
        { id: 'courses', label: t('user.sidebar.myCourses'), icon: 'school', badge: 3 },
        { id: 'vocabulary', label: t('user.sidebar.vocabulary'), icon: 'translate', badge: null },
        { id: 'practice', label: t('user.sidebar.practice'), icon: 'fitness_center', badge: 5 }
      ]
    },
    {
      title: t('user.sidebar.communication'),
      items: [
        { id: 'talk', label: t('user.sidebar.freeTalk'), icon: 'chat', badge: 8 },
        { id: 'virtual-classroom', label: t('user.sidebar.virtualClassroom'), icon: 'video_camera_front', badge: null },
        { id: 'live-learning', label: t('user.sidebar.liveSessions'), icon: 'live_tv', badge: 3 }
      ]
    },
    {
      title: t('user.sidebar.tools'),
      items: [
        { id: 'assignments', label: t('user.sidebar.assignments'), icon: 'assignment', badge: 1 }
      ]
    },
    {
      title: t('user.sidebar.resources'),
      items: [
        { id: 'course-library', label: t('user.sidebar.courseLibrary'), icon: 'library_books', badge: null },
        { id: 'shop', label: t('user.sidebar.shop'), icon: 'shopping_cart', badge: null }
      ]
    },
    {
      title: t('user.sidebar.account'),
      items: [
        { id: 'subscription', label: t('user.sidebar.subscription'), icon: 'card_membership', badge: null },
        { id: 'profile', label: t('user.sidebar.profile'), icon: 'person', badge: null },
        { id: 'account-settings', label: t('user.sidebar.accountSettings'), icon: 'settings', badge: null },
        { id: 'support', label: t('user.sidebar.support'), icon: 'help', badge: null },
        { id: 'live-chat', label: t('user.sidebar.liveChat'), icon: 'chat', badge: null, openChat: true }
      ]
    }
  ];

  return (
    <div className={`user-sidebar ${isOpen ? 'open' : ''}`}>
      {/* Close button for mobile */}
      {onClose && (
        <button 
          className="sidebar-close-btn"
          onClick={onClose}
          aria-label="Close sidebar"
        >
          <i className="material-icons">close</i>
        </button>
      )}
      {/* Header */}
      <div className="user-sidebar-header">
        <div className="user-sidebar-logo-row">
          <div className="user-sidebar-logo">
            <img src={getImageUrl(logoUrl) || logoUrl} alt={t('user.sidebar.appName')} />
            <h2>{t('user.sidebar.appName')}</h2>
          </div>
          <button
            type="button"
            className="user-theme-toggle"
            onClick={toggleTheme}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-pressed={isDark}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <i className="material-icons">{isDark ? 'light_mode' : 'dark_mode'}</i>
          </button>
        </div>

        {/* Profile Section */}
        <div 
          className={`user-profile-section ${activeSection === 'profile' || activeSection === 'profile-settings' ? 'active' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveSection('profile');
          }}
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setActiveSection('profile');
            }
          }}
        >
          <div className="user-profile-picture" onClick={(e) => e.stopPropagation()}>
            {profileImage ? (
              <img src={profileImage} alt={t('user.sidebar.profilePhotoAlt')} />
            ) : (
              <div className="profile-placeholder">{userInitials}</div>
            )}
            <div className="upload-overlay" onClick={(e) => {
              e.stopPropagation();
              setActiveSection('profile');
            }}>
              <i className="material-icons">photo_camera</i>
            </div>
          </div>
          <div className="user-profile-info">
            <h3>{displayName}</h3>
            <p>{displayRole}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="user-sidebar-nav">
        {navSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="user-nav-section">
            <div className="user-nav-section-title">{section.title}</div>
            <ul className="user-nav-list">
              {section.items.map((item) => (
                <li key={item.id} className="user-nav-item">
                  <button
                    className={`user-nav-link ${
                      activeSection === item.id ||
                      (item.id === 'profile' && activeSection === 'profile-settings')
                        ? 'active'
                        : ''
                    } ${item.openChat ? 'user-nav-chat' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      if (item.openChat) {
                        openTawkChat();
                        return;
                      }
                      setActiveSection(item.id);
                    }}
                  >
                    <span className="user-nav-icon">
                      <i className="material-icons">{item.icon}</i>
                    </span>
                    <span className="user-nav-text">{item.label}</span>
                    {item.badge && (
                      <span className="user-nav-badge">{item.badge}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Footer - Removed menu toggle as sidebar is always visible */}
      <div className="user-sidebar-footer">
      </div>
    </div>
  );
};

export default UserSidebar; 